import {useEffect, useState} from "react"
import {useNavigate, useParams} from "react-router-dom"
import type {OtkDocumentDetail} from "shared"

import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,} from "@/components/ui/dialog"
import {Button} from "@/components/ui/button"
import {DocumentModal} from "@/components/documents/DocumentModal"
import {getOtkDocumentById} from "@/lib/api/documents"
import {useDocumentsStore} from "@/store/documents"

/**
 * `/documents/edit/:id` — the dedicated edit-mode route `NewDocumentButton`
 * and the drafts list's own "Редагувати" row action both open. Like
 * `DocumentModalRoute` (view mode), this fetches the fuller
 * `OtkDocumentDetail` via `GET /otk/documents/:id` (`getOtkDocumentById`)
 * itself on mount, since a freshly created draft or a draft on an unvisited
 * tab may not be in the list's own summary `DocumentRow` list at all yet.
 *
 * `detail` backs the header's document number/delete-eligibility and is
 * passed through to `DocumentModal` → `DocumentEdit`, which seeds every
 * tab's local state from it (vehicle/owner/testResults/nonconformities/
 * reconstructions) once it arrives.
 */
export function DocumentEditModalRoute() {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const deleteDraft = useDocumentsStore((s) => s.deleteDraft)
  const refreshCounts = useDocumentsStore((s) => s.refreshCounts)

  const [detail, setDetail] = useState<OtkDocumentDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getOtkDocumentById(id)
      .then((data) => {
        if (cancelled) return
        setDetail(data)
        setError(null)
      })
      .catch(() => {
        if (cancelled) return
        setDetail(null)
        setError("Не вдалося завантажити документ.")
      })
    return () => {
      cancelled = true
    }
  }, [id])

  function handleClose() {
    navigate("/documents", { replace: true })
  }

  if (error) {
    return (
      <Dialog onOpenChange={(open) => !open && handleClose()} open>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Документ ОТК</DialogTitle>
            <DialogDescription>{error}</DialogDescription>
          </DialogHeader>
          <Button onClick={handleClose} variant="outline">
            Назад до документів
          </Button>
        </DialogContent>
      </Dialog>
    )
  }
  return (
    <DocumentModal
      canDelete={detail?.status === "DRAFT"}
      detail={detail}
      documentId={id}
      documentNumber={detail?.documentNumber}
      mode="edit"
      onClose={handleClose}
      onDelete={() => deleteDraft(id, detail?.version ?? 0)}
      onSigned={(document) => {
        setDetail(document)
        void refreshCounts()
      }}
      version={detail?.version}
    />
  )
}
